/// <amd-module name="VSSUI/Components/MasterPanel/MasterPanel" />
import * as React from "react";
import { IMasterDetailsContextLayer } from "VSSUI/Context/MasterDetailsContext.Types";
import { MasterDetailsContext } from "VSSUI/MasterDetailsContext";
import { Observer } from "VSSUI/Observer";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { css } from "VSSUI/Util";
import { SingleLayerMasterPanel, SingleLayerMasterPanelHeader } from "../SingleLayerMasterPanel/SingleLayerMasterPanel";
import { IMasterPanelHeaderProps, IMasterPanelInternalProps, IMasterPanelProps } from "./MasterPanel.Props";
import * as Utils_Accessibility from "VSS/Core/Util/Accessibility";
/**
 * Context-controlled implementation of MasterDetails MasterPanel that allows for arbitrary layers of drill-down
 * If you don't need drill-down behavior, @see SingleLayerMasterPanel instead
 */
export const MasterPanel: React.FunctionComponent<IMasterPanelProps> = props => {
    const { className, showOnSmallScreens } = props;
    const contentElement = React.useRef<HTMLDivElement>(null);
    const masterDetailsContext = React.useContext(MasterDetailsContext);

    return (
        <Surface background={SurfaceBackground.neutral}>
            <Observer masterDetailsLayer={masterDetailsContext.getCurrentLayer()} hideDetailsPanel={masterDetailsContext.hideDetailsPanel}>
                {(observerProps: { masterDetailsLayer: IMasterDetailsContextLayer<any>; hideDetailsPanel: boolean }) => {
                    return (
                        <MasterPanelInternal
                            className={css(className, observerProps.hideDetailsPanel && "full-screen")}
                            layer={observerProps.masterDetailsLayer}
                            showOnSmallScreens={showOnSmallScreens || observerProps.hideDetailsPanel}
                        />
                    );
                }}
            </Observer>
        </Surface>
    );
};

const MasterPanelInternal: React.FunctionComponent<IMasterPanelInternalProps> = props => {
    const { className, layer, showOnSmallScreens } = props;
    const tabStop = React.createRef<HTMLDivElement>();
    const firstUpdate = React.useRef(true);

    React.useEffect(() => {
        // don't set focus on first update
        if (firstUpdate.current) {
            firstUpdate.current = false;
        } else {
            tabStop.current && tabStop.current.focus();
            Utils_Accessibility.announce(layer.ariaLabel);
        }
    }, [props.layer]);

    const masterPanelDetails = layer.masterPanelContent;
    const parentItem = layer.parentItem;
    const selectedItemObservable = layer.selectedMasterItem;

    const renderHeader = masterPanelDetails.renderHeader
        ? () => {
              return masterPanelDetails.renderHeader!(parentItem);
          }
        : undefined;

    const renderSearch = masterPanelDetails.renderSearch
        ? () => {
              return masterPanelDetails.renderSearch!(parentItem);
          }
        : undefined;

    const renderContent = masterPanelDetails.renderContent
        ? () => {
              return masterPanelDetails.renderContent!(parentItem, selectedItemObservable);
          }
        : undefined;

    return (
        <>
             <div ref={tabStop} tabIndex={-1} />
            <SingleLayerMasterPanel
                className={className}
                key={`master-panel-${layer.key}`}
                renderContent={renderContent}
                renderHeader={renderHeader}
                renderSearch={renderSearch}
                showOnSmallScreens={showOnSmallScreens}
            />
        </>
    );
};

export const MasterPanelHeader: React.FunctionComponent<IMasterPanelHeaderProps> = props => {
    const masterDetailsContext = React.useContext(MasterDetailsContext);
    const masterPanelDetails = masterDetailsContext.getCurrentLayer().value.masterPanelContent;

    const onBackClick = () => {
        const shouldPop = masterPanelDetails.onBackButtonClick && masterPanelDetails.onBackButtonClick();

        if (shouldPop !== false) {
            masterDetailsContext.pop();
        }
    };

    return <SingleLayerMasterPanelHeader {...props} onBackButtonClick={masterPanelDetails.hideBackButton !== false ? undefined : onBackClick} />;
};
